import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  GlobalContainer,
  MainContentContainer,
} from "@atlas-int/tokens/layout";
import {
  VisualizationContainer,
  ControlsContainer,
  ControlsInputsRowContainer,
} from "./VizGrid";
import { Outlet, useSearchParams } from "react-router";
import { useIsProductSpace } from "../routing/typedRoutes";
import ControlsPanel from "../components/controls";
import NavigationBar from "../components/navigationBar/NavigationBar";
import { OverlayPortal } from "./Utils";
import { usePreloadMetadata } from "@atlas-int/data";
import { DownloadProvider, useDownload } from "@atlas-int/viz";
import { useTutorial } from "../components/tutorial/TutorialContext";
import TutorialContainer from "../components/tutorial/TutorialContainer";
import MobileNotification from "../components/modals/MobileNotification";
import { useWindowResize } from "@atlas-int/ui";
import VisualizationErrorBoundary from "./VisualizationErrorBoundary";
import { useAssertValidExploreParams } from "./hooks/useAssertValidExploreParams";
import { usePageTracking } from "../sharedUtilities/analytics";
import SurveyWidget from "../sharedUtilities/SurveyWidget";

function ValidatedOutlet() {
  useAssertValidExploreParams();
  return <Outlet />;
}

function VisualizationArea() {
  const { containerRef } = useDownload();
  const isProductSpace = useIsProductSpace();
  const { isTutorialOpen } = useTutorial();
  const [searchParams] = useSearchParams();

  return (
    <VisualizationContainer ref={containerRef}>
      <ControlsContainer data-download-exclude>
        <ControlsInputsRowContainer>
          <ControlsPanel />
        </ControlsInputsRowContainer>
      </ControlsContainer>
      {isProductSpace && isTutorialOpen ? (
        <TutorialContainer />
      ) : (
        <VisualizationErrorBoundary resetKey={searchParams.toString()}>
          <ValidatedOutlet />
        </VisualizationErrorBoundary>
      )}
    </VisualizationContainer>
  );
}

function App() {
  // Track page views on route changes
  usePageTracking();

  // track if small screen notification should be shown
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 700,
  );
  const [notificationClosed, setNotificationClosed] = useState(false);

  const [width] = useWindowResize();
  useEffect(() => {
    if (width !== undefined) {
      setIsMobile(width < 700);
      setNotificationClosed(false);
    }
  }, [width]);

  // Preload commonly used metadata (uses Apollo cache, no Redux needed)
  usePreloadMetadata();

  return (
    <DownloadProvider>
      <GlobalContainer id="root">
        {isMobile &&
          !notificationClosed &&
          createPortal(
            <MobileNotification onClose={() => setNotificationClosed(true)} />,
            document.body,
          )}
        <NavigationBar />
        <MainContentContainer>
          <VisualizationArea />
          <OverlayPortal />
        </MainContentContainer>
        <SurveyWidget />
      </GlobalContainer>
    </DownloadProvider>
  );
}

export default App;
